import React, { forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'

import styled from '@emotion/styled'

import { ClearFieldIcon, ExpandCenteredIcon } from '@arcade/web/icons/react'
import { ArcadeIcon } from '@arcade/web/icons/react'

import { useDebounce } from '../../hooks'
import { ArcadeHelpTextComponentProps, extractHelpTextProps } from '../Arcade/Input/ArcadeHelpText'
import ArcadeInput from '../Arcade/Input/ArcadeInput'
import ArcadeInputDropdownOptions from '../Arcade/InputDropdown/ArcadeInputDropdownOptions'

export interface TypeaheadDropdownProps<T> extends ArcadeHelpTextComponentProps {
  /** Required */
  inputAriaLabel: string
  clearIconAriaLabel: string
  expandIconAriaLabel: string
  getItemKey: (item: T) => string | number
  itemToString: (item: T) => string
  maxDropdownHeightPx: number
  /** Optional */
  debounceMs?: number
  defaultItems?: T[]
  filterItems?: (query: string, items: T[]) => T[]
  isLoading?: boolean
  items?: T[]
  loadingText?: string
  noResultsText?: string
  onBlur?: () => void
  onFocus?: () => void
  onInputChange?: (query: string) => void
  onItemSelected: (item: T | string | null) => void
  placeholder?: string
  testId?: string
  value?: T | string | null
}

const InputDropdownContainer = styled.div`
  position: relative;
`

/** This ensures content is visible to screen readers, but doesn't take up space visually. */
const VisuallyHidden = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

// Default lowercase filter for client-side filtering
const defaultFilterItems = <T,>(query: string, items: T[], itemToString: (item: T) => string): T[] => {
  const lowercaseQuery = query.toLowerCase()
  return items.filter(item => itemToString(item).toLowerCase().includes(lowercaseQuery))
}

function TypeaheadDropdownInner<T>(props: TypeaheadDropdownProps<T>, ref: React.ForwardedRef<HTMLInputElement>) {
  const {
    items = [],
    defaultItems = [],
    getItemKey,
    itemToString,
    value,
    onItemSelected,
    filterItems,
    placeholder,
    inputAriaLabel,
    clearIconAriaLabel,
    expandIconAriaLabel,
    maxDropdownHeightPx,
    noResultsText,
    isLoading = false,
    loadingText,
    debounceMs = 300,
    testId,
    onFocus,
    onBlur,
    onInputChange,
    errorText,
    disabled,
  } = props
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [isEditing, setIsEditing] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  React.useImperativeHandle(ref, () => inputRef.current || ({} as HTMLInputElement), [])
  // Sync query with value when not editing
  useEffect(() => {
    if (!isEditing && value) {
      const valueStr = typeof value === 'string' ? value : itemToString(value)
      setQuery(valueStr)
    }
  }, [value, isEditing, itemToString, query])

  const debouncedQuery = useDebounce(query, debounceMs)

  useEffect(() => {
    if (onInputChange) {
      onInputChange(debouncedQuery)
    }
  }, [debouncedQuery, onInputChange])

  const filteredItems = useMemo(() => {
    if (onInputChange) {
      return items
    }
    return filterItems ? filterItems(debouncedQuery, items) : defaultFilterItems(debouncedQuery, items, itemToString)
  }, [debouncedQuery, filterItems, items, itemToString, onInputChange])

  const displayItems = useMemo(() => {
    if (filteredItems.length > 0) {
      return filteredItems
    }
    if (isEditing && !debouncedQuery && defaultItems.length > 0) {
      return defaultItems
    }
    return []
  }, [filteredItems, isEditing, debouncedQuery, defaultItems])

  const showLoading = isOpen && isLoading
  // Show no results when:
  // 1. User has typed something and no results found (debouncedQuery.length > 0)
  // 2. Items array is empty and we have noResultsText
  const showNoResults =
    isOpen &&
    !isLoading &&
    displayItems.length === 0 &&
    !!noResultsText &&
    (debouncedQuery.length > 0 || (items.length === 0 && isEditing))
  const showResults = isOpen && !isLoading && displayItems.length > 0
  const showDropdown = isOpen && (showLoading || showNoResults || showResults)

  const inputValue = isEditing ? query : value ? (typeof value === 'string' ? value : itemToString(value)) : query

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newQuery = e.target.value
      setQuery(newQuery)
      setHighlightedIndex(-1)

      if (!isOpen && newQuery) {
        setIsOpen(true)
      } else if (isOpen && !newQuery) {
        setIsOpen(false)
      }
    },
    [isOpen]
  )

  const handleInputFocus = useCallback(() => {
    onFocus?.()
    setIsEditing(true)
    setIsOpen(true)
  }, [onFocus])

  const handleInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      /**
       * Handle two distinct selection paths:
       * 1. Manual text entry: When the user types text and blurs without selecting from dropdown,
       *    we treat their typed text as the selected value (string type).
       * 2. Dropdown selection: When user clicks an item from the dropdown, handleSelect is called,
       *    which triggers this blur handler. In this case, we skip selection here since handleSelect
       *    has already called onItemSelected with the item (type T).
       *
       * We detect manual entry by checking if the query differs from the current value's string representation.
       */
      const currentValueStr = typeof value === 'string' ? value : value ? itemToString(value) : ''
      if (query !== currentValueStr) {
        onItemSelected(query)
      }

      setIsOpen(false)
      // Delay to allow the useEffect to sync query with value first
      setTimeout(() => {
        setIsEditing(false)
      }, 0)
      setHighlightedIndex(-1)

      onBlur?.()
    },
    [onBlur, query, value, itemToString, onItemSelected]
  )

  const handleSelect = useCallback(
    (item: T) => {
      onItemSelected(item)
      setQuery(itemToString(item))
      setIsOpen(false)
      setHighlightedIndex(-1)
      inputRef.current?.blur()
    },
    [onItemSelected, itemToString]
  )

  const handleClear = useCallback(() => {
    onItemSelected(null)
    setQuery('')
    setHighlightedIndex(-1)
    inputRef.current?.focus()
  }, [onItemSelected])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showDropdown) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setHighlightedIndex(prev => (prev < displayItems.length - 1 ? prev + 1 : 0))
          break

        case 'ArrowUp':
          e.preventDefault()
          setHighlightedIndex(prev => (prev > 0 ? prev - 1 : displayItems.length - 1))
          break

        case 'Enter':
          e.preventDefault()
          if (highlightedIndex >= 0 && displayItems[highlightedIndex]) {
            handleSelect(displayItems[highlightedIndex])
          }
          break

        case 'Escape':
          e.preventDefault()
          setIsOpen(false)
          setHighlightedIndex(-1)
          break

        case 'Tab':
          // Allow natural tab behavior
          setIsOpen(false)
          setHighlightedIndex(-1)
          break
      }
    },
    [showDropdown, displayItems, highlightedIndex, handleSelect]
  )

  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.querySelector(`[data-index="${highlightedIndex}"]`)
      highlightedElement?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex])

  const statusMessage = useMemo(() => {
    if (!isOpen) return ''
    if (showLoading && loadingText) return loadingText
    if (showNoResults && noResultsText) return noResultsText
    return ''
  }, [isOpen, showLoading, showNoResults, loadingText, noResultsText])

  const showClearButton = !!value
  const trailingIcon = showClearButton ? ClearFieldIcon : ExpandCenteredIcon

  const listboxId = `arcade-dropdown-list-${useId()}`
  const helpTextProps = extractHelpTextProps(props)

  return (
    <InputDropdownContainer ref={containerRef} data-testid={testId ? `${testId}-container` : undefined}>
      {/* Visually hidden live region for status announcements - always present */}
      <VisuallyHidden role="status" aria-live="polite">
        {statusMessage}
      </VisuallyHidden>

      <ArcadeInput
        ref={inputRef}
        value={inputValue}
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={showDropdown ? listboxId : undefined}
        aria-haspopup="listbox"
        aria-label={inputAriaLabel}
        aria-autocomplete="list"
        aria-activedescendant={highlightedIndex >= 0 ? `arcade-dropdown-option-${highlightedIndex}` : undefined}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        {...helpTextProps}
        disabled={disabled}
        isInvalid={!!errorText}
        TrailingIcon={trailingIcon as ArcadeIcon}
        trailingIconClickProps={{
          onClick: showClearButton ? handleClear : () => setIsOpen(!isOpen),
          ariaLabel: showClearButton ? clearIconAriaLabel : expandIconAriaLabel,
        }}
        // Some browsers ignore "off" but respect "new-password"
        // This is a common workaround to prevent browser autocomplete
        autoComplete="new-password"
        data-testid={testId ? `${testId}-input` : undefined}
      />

      {showDropdown && (
        <div ref={dropdownRef}>
          <ArcadeInputDropdownOptions
            items={displayItems}
            getItemKey={getItemKey}
            itemToString={itemToString}
            maxDropdownHeightPx={maxDropdownHeightPx}
            highlightedIndex={highlightedIndex}
            isLoading={showLoading}
            loadingText={loadingText}
            noResultsText={showNoResults ? noResultsText : undefined}
            selectedValue={value}
            testId={testId}
            listboxId={listboxId}
            onItemClick={handleSelect}
            onItemMouseEnter={setHighlightedIndex}
          />
        </div>
      )}
    </InputDropdownContainer>
  )
}

export const TypeaheadDropdown = forwardRef(TypeaheadDropdownInner) as <T>(
  props: TypeaheadDropdownProps<T> & React.RefAttributes<HTMLInputElement>
) => React.ReactElement

export default TypeaheadDropdown
